/**
 * Game sounds. Every sound has a name. If a file with that name exists in
 * assets/sounds/ (mp3, m4a, ogg or wav), it is played; otherwise the sound
 * is synthesised here with the Web Audio API (bubbles, water drops, chimes).
 *
 * To replace a sound: upload e.g. assets/sounds/pop.mp3 – nothing else.
 * Pitch and volume variation still apply to replacement files.
 */
export const SOUND_NAMES = [
  'tap', 'swap', 'invalid', 'pop', 'drop', 'stripe', 'bomb', 'colorstar', 'create',
  'combo', 'mud', 'stain', 'shuffle', 'goal', 'win', 'lose', 'button', 'praise',
] as const;
export type SoundName = (typeof SOUND_NAMES)[number];

export interface PlayOptions {
  /** playback-rate multiplier, 1 = normal */
  pitch?: number;
  /** 0..1 */
  volume?: number;
}

/** C-major pentatonic steps, used to raise the pop pitch with every cascade */
const PENTATONIC = [0, 2, 4, 7, 9, 12, 14, 16, 19, 21];
export const cascadePitch = (cascade: number) => 2 ** (PENTATONIC[Math.min(cascade - 1, PENTATONIC.length - 1)] / 12);

type Synth = (s: SoundEngine, t: number, pitch: number, vol: number) => void;

class SoundEngine {
  ctx: AudioContext;
  master: GainNode;
  private noiseBuf?: AudioBuffer;

  constructor() {
    this.ctx = new AudioContext();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.7;
    this.master.connect(this.ctx.destination);
  }

  noise(): AudioBuffer {
    if (!this.noiseBuf) {
      const b = this.ctx.createBuffer(1, this.ctx.sampleRate, this.ctx.sampleRate);
      const d = b.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
      this.noiseBuf = b;
    }
    return this.noiseBuf;
  }

  /** simple percussive envelope into the master bus */
  env(t: number, attack: number, decay: number, peak: number): GainNode {
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0002), t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
    g.connect(this.master);
    return g;
  }

  tone(type: OscillatorType, f0: number, f1: number, t: number, dur: number, peak: number, attack = 0.005): void {
    const o = this.ctx.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(f1, t + dur);
    o.connect(this.env(t, attack, dur, peak));
    o.start(t);
    o.stop(t + attack + dur + 0.05);
  }

  hiss(filter: BiquadFilterType, f0: number, f1: number, t: number, dur: number, peak: number, q = 1): void {
    const src = this.ctx.createBufferSource();
    src.buffer = this.noise();
    const f = this.ctx.createBiquadFilter();
    f.type = filter;
    f.Q.value = q;
    f.frequency.setValueAtTime(f0, t);
    f.frequency.exponentialRampToValueAtTime(f1, t + dur);
    src.connect(f).connect(this.env(t, 0.01, dur, peak));
    src.start(t, Math.random() * 0.5);
    src.stop(t + dur + 0.05);
  }
}

const note = (semitonesFromA4: number) => 440 * 2 ** (semitonesFromA4 / 12);
const C5 = note(3), E5 = note(7), G5 = note(10), C6 = note(15), E6 = note(19), G6 = note(22), C7 = note(27);

/** The built-in sound designs. Short, soft, rounded – nothing harsh. */
const SYNTHS: Record<SoundName, Synth> = {
  tap: (s, t, p, v) => s.tone('sine', 880 * p, 1100 * p, t, 0.05, 0.12 * v),
  button: (s, t, p, v) => s.tone('sine', 620 * p, 520 * p, t, 0.05, 0.14 * v),
  swap: (s, t, p, v) => {
    s.tone('sine', 380 * p, 640 * p, t, 0.12, 0.14 * v);
    s.tone('triangle', 760 * p, 1280 * p, t, 0.08, 0.03 * v);
  },
  invalid: (s, t, p, v) => {
    s.tone('triangle', 330 * p, 280 * p, t, 0.08, 0.14 * v);
    s.tone('triangle', 262 * p, 220 * p, t + 0.1, 0.1, 0.14 * v);
  },
  // bubble "bloop": fast upward sweep with a quick decay, plus a tiny echo bubble
  pop: (s, t, p, v) => {
    s.tone('sine', 360 * p, 980 * p, t, 0.09, 0.32 * v, 0.004);
    s.tone('sine', 520 * p, 1400 * p, t + 0.035, 0.06, 0.12 * v, 0.003);
  },
  // water drop "plink": high, very short downward flick
  drop: (s, t, p, v) => {
    s.tone('sine', 1900 * p, 850 * p, t, 0.05, 0.08 * v, 0.002);
    s.tone('sine', 1250 * p, 1500 * p, t + 0.02, 0.07, 0.04 * v, 0.002);
  },
  stripe: (s, t, p, v) => {
    s.hiss('bandpass', 500 * p, 5200 * p, t, 0.32, 0.35 * v, 1.4);
    s.tone('sine', 300 * p, 1300 * p, t, 0.25, 0.1 * v);
  },
  bomb: (s, t, p, v) => {
    s.tone('sine', 170 * p, 42 * p, t, 0.34, 0.55 * v, 0.004);
    s.hiss('lowpass', 1400 * p, 180 * p, t, 0.4, 0.3 * v);
    for (let i = 0; i < 4; i++) s.tone('sine', (500 + i * 170) * p, (1300 + i * 250) * p, t + 0.08 + i * 0.045, 0.07, 0.1 * v);
  },
  colorstar: (s, t, p, v) => {
    [C6, E6, G6, C7].forEach((f, i) => s.tone('triangle', f * p, f * p * 1.01, t + i * 0.06, 0.22, 0.12 * v));
    s.hiss('highpass', 6000, 9000, t, 0.5, 0.05 * v);
  },
  create: (s, t, p, v) => {
    s.tone('sine', G6 * p, G6 * p, t, 0.35, 0.1 * v);
    s.tone('sine', C7 * p, C7 * p, t + 0.07, 0.4, 0.08 * v);
  },
  combo: (s, t, p, v) => {
    SYNTHS.bomb(s, t, p * 0.9, v);
    SYNTHS.colorstar(s, t + 0.1, p, v);
  },
  // squelchy mud: filtered noise with a low wobble
  mud: (s, t, p, v) => {
    s.hiss('lowpass', 900 * p, 250 * p, t, 0.18, 0.35 * v, 4);
    s.tone('sine', 150 * p, 80 * p, t, 0.14, 0.25 * v);
  },
  stain: (s, t, p, v) => {
    for (let i = 0; i < 3; i++) s.tone('sine', (900 + i * 220) * p, (2000 + i * 300) * p, t + i * 0.05, 0.05, 0.09 * v, 0.003);
  },
  shuffle: (s, t, p, v) => {
    for (let i = 0; i < 7; i++) s.tone('sine', (500 + i * 90) * p, (700 + i * 90) * p, t + i * 0.05, 0.04, 0.08 * v);
  },
  goal: (s, t, p, v) => {
    s.tone('triangle', E6 * p, E6 * p, t, 0.25, 0.12 * v);
    s.tone('triangle', G6 * p, G6 * p, t + 0.09, 0.35, 0.12 * v);
  },
  praise: (s, t, p, v) => {
    [G5, C6, E6].forEach((f, i) => s.tone('sine', f * p, f * p, t + i * 0.05, 0.25, 0.1 * v));
  },
  win: (s, t, p, v) => {
    [C5, E5, G5, C6].forEach((f, i) => s.tone('triangle', f * p, f * p, t + i * 0.12, 0.3, 0.16 * v));
    [C6, E6, G6].forEach((f) => s.tone('sine', f * p, f * p, t + 0.5, 0.9, 0.08 * v, 0.02));
  },
  // hopeful, not sad: a soft two-note "aww, almost"
  lose: (s, t, p, v) => {
    s.tone('sine', G5 * p, G5 * p, t, 0.28, 0.12 * v, 0.02);
    s.tone('sine', E5 * p, E5 * p, t + 0.24, 0.45, 0.12 * v, 0.02);
  },
};

class SoundBank {
  private engine?: SoundEngine;
  private files: Partial<Record<SoundName, string>> = {};
  private buffers = new Map<SoundName, AudioBuffer>();
  private last = new Map<SoundName, number>();
  muted = false;

  /** Registers replacement files from the build manifest (see tools/build-atlas.mjs). */
  setFiles(files: Record<string, string>): void {
    this.files = files as Partial<Record<SoundName, string>>;
  }

  /**
   * Browsers only allow audio after a user gesture. Call on the first tap;
   * iOS additionally needs a (silent) sound started inside that gesture.
   */
  unlock(): void {
    if (!this.engine) {
      try {
        // respect the iPhone silent switch like other casual games do
        const session = (navigator as { audioSession?: { type: string } }).audioSession;
        if (session) session.type = 'ambient';
        this.engine = new SoundEngine();
        void this.loadFiles();
      } catch {
        return; // no Web Audio: play silently
      }
    }
    const ctx = this.engine.ctx;
    if (ctx.state !== 'running') void ctx.resume();
    const src = ctx.createBufferSource();
    src.buffer = ctx.createBuffer(1, 1, 22050);
    src.connect(ctx.destination);
    src.start();
  }

  private async loadFiles(): Promise<void> {
    const ctx = this.engine!.ctx;
    await Promise.all(Object.entries(this.files).map(async ([name, url]) => {
      try {
        const data = await (await fetch(url!)).arrayBuffer();
        this.buffers.set(name as SoundName, await ctx.decodeAudioData(data));
      } catch (e) {
        console.warn(`sound file for "${name}" could not be loaded, using built-in`, e);
      }
    }));
  }

  play(name: SoundName, opts: PlayOptions = {}): void {
    const e = this.engine;
    if (this.muted || !e || e.ctx.state !== 'running') return;
    // the same sound at most every 35 ms (20 tiles popping = one pop)
    const now = performance.now();
    if (now - (this.last.get(name) ?? 0) < 35) return;
    this.last.set(name, now);

    const pitch = (opts.pitch ?? 1) * (0.97 + Math.random() * 0.06);
    const vol = opts.volume ?? 1;
    const buf = this.buffers.get(name);
    if (buf) {
      const src = e.ctx.createBufferSource();
      src.buffer = buf;
      src.playbackRate.value = pitch;
      const g = e.ctx.createGain();
      g.gain.value = vol;
      src.connect(g).connect(e.master);
      src.start();
    } else {
      SYNTHS[name](e, e.ctx.currentTime + 0.005, pitch, vol);
    }
  }
}

export const sound = new SoundBank();
